import { Component } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import Media from 'components/utils/Media'

import fetchUser from '../../actions/userActions'

import Container from '../../components/layout/Container'
import Spinner from '../../components/display/Spinner'
import SimpleA from '../../components/display/SimpleAnchor'

import StoryCard from '../../components/cards/StoryCard'
import { Content } from '../../components/cards/shared/styled'
import ListingCard from '../../components/cards/ListingCard'
import Review from '../../components/cards/shared/Review'

import InstagramIcon from '../../components/icons/Instagram'
import LocationPinIcon from '../../components/icons/LocationPin'
import ValidIcon from '../../components/icons/Valid'

import ShowMoreText from '../../components/ShowMoreText'

const Wrapper = styled.div`
  .row {
    display: flex;
    flex-wrap: wrap;
  }
`

const SpinnerWrapper = styled.div`
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Section = styled.section`
  padding: 40px 0;
  margin: 0 !important;
`

const SectionTitle = styled.h2`
  margin: 0;
  margin-bottom: 40px;
  font-size: 28px;
  line-height: 1;
`

const Counter = styled.span`
  position: relative;
  top: -4px;
  display: inline-block;
  width: 24px;
  height: 24px;
  margin-left: 12px;

  font-size: 16px;
  font-weight: normal;
  line-height: 24px;
  text-align: center;

  border-radius: 4px;
  color: white;
  background-color: ${({ theme }) => theme.brand.jungle};
`

const IntroSection = styled.div`
  display: flex;
`

const IntroTitle = styled.h1`
  font-size: 32px;
  margin: 0;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    font-size: 42px;
  }
`

const AvatarWrapper = styled.div`
  padding-right: 20px;
  @media (min-width: 768px) {
    padding-right: 68px;
  }
`

const Avatar = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: black;
  margin-bottom: 20px;

  float: left;
  margin-right: 20px;

  @media (min-width: 768px) {
    float: none;
    margin-right: 0;
    width: 200px;
    height: 200px;
  }
`

const AttributesWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-bottom: 20px;
  @media (min-width: 768px) {
    flex-direction: row;
  }
`

const UserAttribute = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
  margin-right: 20px;
  color: ${({ theme, colorful }) =>
    colorful ? theme['-ds-core-400'].value : theme.colors.labelGrey};

  svg {
    color: ${({ theme }) => theme['-ds-core-400'].value};
    height: 18px;
    margin-right: 8px;
  }
`

const CardWrapper = styled.div`
  position: relative;

  ${Content} {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
`

export class UserProfile extends Component {
  componentDidMount() {
    this.ensureUserLoaded()
    this.updateTitle()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.userId !== this.props.match.params.userId) {
      this.ensureUserLoaded()
    }

    this.updateTitle()
  }

  // The server seeds the matching user, so only fetch when it's missing or the
  // route points at a different user (client-side navigation between profiles).
  ensureUserLoaded() {
    const { dispatch, match, user } = this.props
    const { userId } = match.params
    const loaded = user.data && String(user.data.id) === String(userId)

    if (!loaded) {
      dispatch(fetchUser(userId))
    }
  }

  updateTitle() {
    const { user } = this.props

    if (user.data && typeof document !== 'undefined') {
      document.title = `${user.data.fullname} | Riparide`
    }
  }

  renderStory = (story) => {
    const { listing } = story.featured_listing

    return (
      <CardWrapper className="col-xs-12 col-sm-6 col-md-4">
        <StoryCard story={story} listing={listing} showBadge={false} />
      </CardWrapper>
    )
  }

  render() {
    const { user } = this.props

    if (user.fetching || !user.data) {
      return (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      )
    }

    return (
      <Wrapper>
        <Container>
          <Section>
            <IntroSection>
              <Media query="(min-width: 768px)">
                <AvatarWrapper>
                  <Avatar src={user.data.avatar_url} />
                </AvatarWrapper>
              </Media>
              <div style={{ flex: 1 }}>
                <Media query="(max-width: 767px)">
                  <Avatar src={user.data.avatar_url} />
                </Media>
                <IntroTitle>Hi, I&apos;m {user.data.first_name}</IntroTitle>
                <AttributesWrapper>
                  {user.data.location && (
                    <UserAttribute>
                      <LocationPinIcon /> {user.data.location}
                    </UserAttribute>
                  )}
                  {user.data.email_verified && (
                    <UserAttribute>
                      <ValidIcon /> Email Verified
                    </UserAttribute>
                  )}
                  {user.data.phone_number_verified && (
                    <UserAttribute>
                      <ValidIcon /> Phone Verified
                    </UserAttribute>
                  )}
                  {user.data.facebook_verified && (
                    <UserAttribute>
                      <ValidIcon /> Facebook Verified
                    </UserAttribute>
                  )}
                  {user.data?.instagram && (
                    <SimpleA
                      href={`https://instagram.com/${user.data.instagram}`}
                      target="_blank"
                      textDecoration="none"
                    >
                      <UserAttribute colorful>
                        <InstagramIcon /> @{user.data.instagram}
                      </UserAttribute>
                    </SimpleA>
                  )}
                </AttributesWrapper>
                <ShowMoreText>{user.data.description}</ShowMoreText>
              </div>
            </IntroSection>
          </Section>

          {user.data.stories.length > 0 && (
            <Section>
              <SectionTitle>
                Stories <Counter>{user.data.stories.length}</Counter>
              </SectionTitle>
              <div className="row">
                {user.data.stories.map(this.renderStory)}
              </div>
            </Section>
          )}

          {user.data.listings.length > 0 && (
            <Section>
              <SectionTitle>
                Listings <Counter>{user.data.listings.length}</Counter>
              </SectionTitle>
              <div className="row">
                {user.data.listings.map((listing) => (
                  <CardWrapper className="col-sm-6 col-md-4" key={listing.id}>
                    <ListingCard listing={listing} />
                  </CardWrapper>
                ))}
              </div>
            </Section>
          )}

          {user.data.reviews.length > 0 && (
            <Section>
              <SectionTitle>
                Reviews <Counter>{user.data.reviews.length}</Counter>
              </SectionTitle>
              <div>
                {user.data.reviews.map((review) => (
                  <Review key={review.id} review={review} />
                ))}
              </div>
            </Section>
          )}
        </Container>
      </Wrapper>
    )
  }
}

export default connect(({ user }) => ({ user }))(UserProfile)
